import React, { useState, FormEvent } from "react";
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Container,
    Grid,
    TextField,
    Typography,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import { createContact, ContactFormData } from "@services/contact.service";
import SectionTitle from "@others/title-section.layout";
import ReCAPTCHA from "react-google-recaptcha";

const ContactPage: React.FC = () => {
    const [formData, setFormData] = useState<ContactFormData>({
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        subject: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState<string>("");
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const onCaptchaChange = (token: string | null) => {
        setCaptchaToken(token);
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!captchaToken) {
            alert("Veuillez vérifier que vous n'êtes pas un robot");
            return;
        }
        setSubmitting(true);
        try {
            await createContact(formData);
            setFeedbackMessage(
                "Votre message a été envoyé avec succès. Nous vous répondrons dès que possible."
            );
            // Réinitialiser le formulaire si besoin
            setFormData({
                firstName: "",
                lastName: "",
                email: "",
                phoneNumber: "",
                subject: "",
                message: "",
            });
        } catch (error) {
            setFeedbackMessage("Une erreur est survenue. Veuillez réessayer.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Container>
            <SectionTitle title={"CONTACTEZ-NOUS"} />

            {feedbackMessage ? (
                // Affichage de la Card avec le message de feedback et le lien de retour
                <Card sx={{ marginBottom: "30px", padding: 2 }}>
                    <CardContent>
                        <Box textAlign="center" mb={3}>
                            <FontAwesomeIcon icon={faEnvelope} size="2x" />
                            <Typography variant="h5" mt={1}>
                                {feedbackMessage}
                            </Typography>
                        </Box>
                    </CardContent>
                    <CardActions sx={{ justifyContent: "center" }}>
                        <Button component={Link} to="/" variant="outlined">
                            Revenir vers l'accueil
                        </Button>
                    </CardActions>
                </Card>
            ) : (
                // Affichage du formulaire de contact
                <form onSubmit={handleSubmit}>
                    <Card sx={{ marginBottom: "30px", padding: 2 }}>
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12 }}>
                                    <Box textAlign="center" mb={3}>
                                        <FontAwesomeIcon icon={faEnvelope} size="2x" />
                                        <Typography variant="h5" mt={1}>
                                            Formulaire de contact
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        label="Prénom"
                                        name="firstName"
                                        variant="outlined"
                                        fullWidth
                                        margin="normal"
                                        required
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        sx={{ boxShadow: 2 }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        label="Nom"
                                        name="lastName"
                                        variant="outlined"
                                        fullWidth
                                        margin="normal"
                                        required
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        sx={{ boxShadow: 2 }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        label="Email"
                                        name="email"
                                        type="email"
                                        variant="outlined"
                                        fullWidth
                                        margin="normal"
                                        required
                                        value={formData.email}
                                        onChange={handleChange}
                                        sx={{ boxShadow: 2 }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        label="Téléphone"
                                        name="phoneNumber"
                                        variant="outlined"
                                        fullWidth
                                        margin="normal"
                                        value={formData.phoneNumber}
                                        onChange={handleChange}
                                        sx={{ boxShadow: 2 }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <TextField
                                        label="Sujet"
                                        name="subject"
                                        variant="outlined"
                                        fullWidth
                                        margin="normal"
                                        required
                                        value={formData.subject}
                                        onChange={handleChange}
                                        sx={{ boxShadow: 2 }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <TextField
                                        label="Message"
                                        name="message"
                                        variant="outlined"
                                        fullWidth
                                        margin="normal"
                                        required
                                        multiline
                                        rows={4}
                                        value={formData.message}
                                        onChange={handleChange}
                                        sx={{ boxShadow: 2 }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }} sx={{ display: "flex", justifyContent: "center" }}>
                                    <ReCAPTCHA
                                        sitekey="6Le2gR4rAAAAAN7NYQGr9dxJE-qTbpHeMyf3oAKZ"
                                        onChange={onCaptchaChange}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                        <CardActions sx={{ justifyContent: "center", paddingBottom: 2 }}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={submitting}
                            >
                                {submitting ? "Envoi en cours..." : "Envoyer"}
                            </Button>
                        </CardActions>
                    </Card>
                </form>
            )}
        </Container>
    );
};

export default ContactPage;